import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config';
import { validateUser } from './validation/validators';
import { handleAuthError } from './handlers/authError';
import type { UserData } from './types';

export async function getUserData(userId: string): Promise<UserData | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    
    // Convert timestamps to dates
    const userData = {
      ...data,
      id: userId,
      photoURL: data.photoURL || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      roles: Array.isArray(data.roles) ? data.roles.map(role => ({
        ...role,
        assignedAt: role.assignedAt?.toDate() || new Date()
      })) : [{
        role: 'customer',
        assignedAt: new Date(),
        assignedBy: 'system',
        isActive: true
      }]
    };

    // Validate and return the user data
    return validateUser(userData);
  } catch (error) {
    throw handleAuthError(error);
  }
}

export async function checkUserExists(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    return userDoc.exists();
  } catch (error) {
    console.error('Error checking user existence:', error);
    return false;
  }
}

export async function findUserByPhone(phoneNumber: string): Promise<UserData | null> {
  try {
    // Try different phone number formats
    const phoneFormats = [
      phoneNumber, // Original format
      phoneNumber.replace('+966', '0'), // +966568403040 -> 0568403040
      phoneNumber.replace('+966', ''), // +966568403040 -> 568403040
      phoneNumber.startsWith('0') ? '+966' + phoneNumber.substring(1) : null, // 0568403040 -> +966568403040
      phoneNumber.startsWith('966') ? '+' + phoneNumber : null, // 966568403040 -> +966568403040
      phoneNumber.startsWith('966') ? '0' + phoneNumber.substring(3) : null, // 966568403040 -> 0568403040
    ].filter(Boolean); // Remove null values
    
    const usersRef = collection(db, 'users');
    
    // Try each format
    for (const format of phoneFormats) {
      const q = query(usersRef, where('phone', '==', format));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userId = userDoc.id;
        const data = userDoc.data();
        
        // Convert timestamps to dates
        const userData = {
          ...data,
          id: userId,
          photoURL: data.photoURL || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          roles: Array.isArray(data.roles) ? data.roles.map(role => ({
            ...role,
            assignedAt: role.assignedAt?.toDate() || new Date()
          })) : [{
            role: 'customer',
            assignedAt: new Date(),
            assignedBy: 'system',
            isActive: true
          }]
        };

        // Validate and return the user data
        return validateUser(userData);
      }
    }
    
    return null;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * Create a minimal `users` doc for a brand-new phone-OTP customer.
 * Only the request-treatment flow may invoke this (see
 * verifyWhatsAppOTP).
 *
 * Returns the same shape `findUserByPhone` returns so callers don't
 * branch on origin.
 */
export async function createCustomerStub(phone: string): Promise<UserData> {
  const usersRef = collection(db, 'users');
  const docRef = await addDoc(usersRef, {
    phone,
    roles: [{ role: 'customer', isActive: true, assignedAt: new Date(), assignedBy: 'landing-request-treatment' }],
    createdAt: serverTimestamp(),
    source: 'landing-request-treatment',
  });
  return {
    id: docRef.id,
    phone,
    roles: [{ role: 'customer', isActive: true }],
    photoURL: '',
    createdAt: new Date(),
  } as unknown as UserData;
}